import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  Phone,
  Mail,
  Facebook,
  Twitter,
  Instagram,
  Linkedin,
  Menu,
  X,
  ChevronDown,
} from "lucide-react";
import { companyInfo } from "../data/mockData";

const navLinks = [
  { label: "Home", href: "/" },
  {
    label: "Services",
    href: "/services",
    submenu: [
      { label: "FDM Printing", href: "/services/fdm" },
      { label: "SLA Printing", href: "/services/sla" },
      { label: "SLS Printing", href: "/services/sls" },
      { label: "Product Development", href: "/services/product-development" },
      { label: "Post-Processing", href: "/services/post-processing" },
    ],
  },
  { label: "Projects", href: "/projects" },
  { label: "About", href: "/about" },
  { label: "Blog", href: "/blog" },
  { label: "Contact", href: "/contact" },
  { label: "Careers", href: "/careers" },
];

const TopBar = () => (
  <div className="topbar">
    <div className="topbar-inner">
      <div className="topbar-left">
        <a href={`tel:${companyInfo.phone}`} className="topbar-item">
          <Phone size={14} />
          <span>{companyInfo.phone}</span>
        </a>
        <span className="topbar-divider">|</span>
        <a href={`mailto:${companyInfo.email}`} className="topbar-item">
          <Mail size={14} />
          <span>{companyInfo.email}</span>
        </a>
      </div>
      <div className="topbar-right">
        <div className="topbar-social">
          <a href="#" aria-label="Facebook"><Facebook size={14} /></a>
          <a href="#" aria-label="Twitter"><Twitter size={14} /></a>
          <a href="#" aria-label="Instagram"><Instagram size={14} /></a>
          <a href="#" aria-label="LinkedIn"><Linkedin size={14} /></a>
        </div>
      </div>
    </div>
  </div>
);

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [activeSubmenu, setActiveSubmenu] = useState(null);
  const navRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 80);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setActiveSubmenu(null);
  }, [location.pathname]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (navRef.current && !navRef.current.contains(e.target)) {
        setActiveSubmenu(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleNavClick = (href) => {
    setMobileOpen(false);
    setActiveSubmenu(null);
    // If it's a hash link on the home page
    if (href.startsWith("#")) {
      if (location.pathname === "/") {
        const el = document.querySelector(href);
        if (el) el.scrollIntoView({ behavior: "smooth" });
      } else {
        navigate("/" + href);
      }
    } else {
      navigate(href);
    }
  };

  return (
    <nav ref={navRef} className={`navbar ${scrolled ? "navbar-scrolled" : ""}`}>
      <div className="navbar-inner">
        <Link to="/" className="navbar-logo">
          <div className="logo-icon">
            <svg viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M20 4L36 12V28L20 36L4 28V12L20 4Z" stroke="#c54b2a" strokeWidth="2" fill="none" />
              <path d="M20 4L36 12L20 20L4 12L20 4Z" fill="#c54b2a" opacity="0.3" />
              <path d="M20 20V36" stroke="#c54b2a" strokeWidth="2" />
              <path d="M4 12L20 20" stroke="#c54b2a" strokeWidth="1.5" />
              <path d="M36 12L20 20" stroke="#c54b2a" strokeWidth="1.5" />
            </svg>
          </div>
          <div className="logo-text">
            <span className="logo-title">3D Printzkart</span>
            <span className="logo-subtitle">ADDITIVE MANUFACTURING</span>
          </div>
        </Link>

        <div className={`navbar-links ${mobileOpen ? "navbar-links-open" : ""}`}>
          {navLinks.map((link, i) => (
            <div
              key={i}
              className="nav-item"
              onMouseEnter={() => link.submenu && setActiveSubmenu(i)}
              onMouseLeave={() => setActiveSubmenu(null)}
            >
              <button
                className={`nav-link ${location.pathname === link.href ? 'nav-active' : ''}`}
                onClick={() => {
                  if (link.submenu) {
                    // On desktop, navigate to the link; on mobile, toggle submenu
                    if (window.innerWidth > 992) {
                      handleNavClick(link.href);
                    } else {
                      setActiveSubmenu(activeSubmenu === i ? null : i);
                    }
                  } else {
                    handleNavClick(link.href);
                  }
                }}
              >
                {link.label}
                {link.submenu && <ChevronDown size={14} className="nav-chevron" />}
              </button>
              {link.submenu && activeSubmenu === i && (
                <div className="nav-submenu">
                  {link.submenu.map((sub, j) => (
                    <button key={j} className="nav-submenu-link" onClick={() => handleNavClick(sub.href)}>
                      {sub.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="navbar-right">
          <a href={`tel:${companyInfo.phone}`} className="navbar-toll">
            <div className="toll-icon">
              <Phone size={16} />
            </div>
            <span>{companyInfo.tollFree}</span>
          </a>
          <button
            className="mobile-toggle"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label="Toggle menu"
          >
            {mobileOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>
    </nav>
  );
};

const Header = () => (
  <>
    <TopBar />
    <Navbar />
  </>
);

export default Header;
